import { useRef } from 'react';
import { useDialog } from 'react-aria';
import { useOverlay, useModal, OverlayContainer, FocusScope } from 'react-aria';
import * as S from './modal.styles';
import { ModalProps } from './modal.types';
import { IconButton } from '@design-system/atoms/icon-button';

/**
 * Modal component with accessibility features
 */
export const Modal = ({
  isOpen,
  onClose,
  onRequestClose,
  title,
  titleVariant = 'h3',
  subtitle,
  children,
  width = 500,
  className,
  isConfirmation,
  isClosable = true,
}: ModalProps) => {
  // Refs for the DOM elements
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleOverlayClose = () => {
    if (!isClosable) return;

    if (onRequestClose) {
      onRequestClose('overlay');
      return;
    }
    onClose();
  };

  const handleHeaderClose = () => {
    if (!isClosable) return;

    if (onRequestClose) {
      onRequestClose('header');
      return;
    }
    onClose();
  };

  // Handle overlay interactions
  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen,
      onClose: handleOverlayClose,
      isDismissable: isClosable,
      shouldCloseOnInteractOutside: (element) => !element.closest('[data-tooltip-layer]'),
    },
    overlayRef
  );

  // Handle modal-specific behaviors
  const { modalProps } = useModal();

  // Handle dialog-specific behaviors
  const { dialogProps, titleProps } = useDialog({}, dialogRef);

  // If the modal is not open, don't render anything
  if (!isOpen) return null;

  const originalOnKeyDown = overlayProps.onKeyDown;
  const handleKeyDown: typeof originalOnKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();

      if (!isClosable) return; // block ESC

      if (onRequestClose) {
        onRequestClose('escape');
        return;
      }
      onClose();
      return;
    }
    originalOnKeyDown?.(event);
  };

  return (
    <OverlayContainer>
      <S.Underlay {...underlayProps} data-overlay-layer>
        <FocusScope contain restoreFocus autoFocus>
          <S.ModalWrapper
            {...overlayProps}
            {...dialogProps}
            {...modalProps}
            ref={overlayRef}
            style={{ width: `${width}px` }}
            className={className}
            onKeyDown={handleKeyDown}
          >
            {!isConfirmation && (
              <S.ModalHeader $withSubtitle={Boolean(subtitle)}>
                <S.ModalHeaderMain>
                  {title != null && title !== '' && (
                    <S.ModalTitle variant={titleVariant} as="h2" {...titleProps}>
                      {title}
                    </S.ModalTitle>
                  )}
                  {subtitle}
                </S.ModalHeaderMain>

                {isClosable && (
                  <IconButton
                    size={16}
                    variant="circle"
                    icon="close"
                    onPress={handleHeaderClose}
                    aria-label="Close"
                  />
                )}
              </S.ModalHeader>
            )}
            <S.ModalContent ref={dialogRef}>{children}</S.ModalContent>
          </S.ModalWrapper>
        </FocusScope>
      </S.Underlay>
    </OverlayContainer>
  );
};
